// Modo "Animar imagen" — pantalla propia (ruta /animar-imagen).
//
// Promueve a modo la función que hasta ahora sólo existía como un botón adentro
// del historial (GeneratedImagesHistory) y del visor inline (GeneratedImagesInline),
// donde no la encontraba nadie que no abriera una imagen generada. Suma además un
// caso nuevo: animar una FOTO PROPIA, no sólo lo que generó Atlas.
//
// UI: espeja el layout de los otros modos (PresetDetailSheet) para que se sienta
// parte de la misma familia, aunque sea una ruta y no un sheet:
//   • desktop → panel fijo a la derecha de la sidebar (bg gris + esquinas
//     redondeadas), header con "Volver", y DOS columnas: carátula + texto a la
//     izquierda, card blanca de opciones a la derecha.
//   • mobile  → carátula full-bleed arriba y panel blanco redondeado abajo
//     (-mt-6 rounded-t-3xl), con el back flotante del shell (mobileBackOverride,
//     que además esconde la bottom nav).
//
// Dos pantallas: (1) elegir la imagen — subir foto o elegir una generada;
// (2) configurar y generar — descripción sugerida, duración, múltiples escenas.
// Al generar: pantalla de carga estándar → historial con el video, igual que el
// resto de las generaciones. La foto subida NO se registra en `imagenes_generadas`
// (ver lib/animarImagenUpload). Ver ADR-2026-08-14-modo-animar-imagen.
//
// OJO con los colores: las clases token-con-opacidad (`text-brand-black/90`,
// `bg-brand-primary/10`) NO compilan en este Tailwind — quedan sin regla en el CSS.
// Por eso acá se usa `text-brand-black` pelado y hex arbitrario con alpha
// (`text-[#3C3C43]/60`, `bg-[#1644EB]/10`), que sí compilan.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ImageUp,
  Images,
  Info,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useSidebar } from '../../../context/SidebarContext';
import CachedImg from '../../../components/CachedImg';
import LoadingAnimation from '../../../components/LoadingAnimation';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { CreditBadge } from '../../../components/CreditBadge';
import { isProOrUltra } from '../../../lib/planCapabilities';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  SUBIDA_REQUIERE_ATLAS,
  buildAnimarVideoBody,
  fetchSuggestedVideoPromptForSource,
  generateVideoFromImage,
  type AnimarSource,
} from '../../../lib/videoFromImage';
import {
  GeneradasPickGrid,
  type GeneradaPick,
} from './shared/GeneradasPickGrid';

/** Carátula del modo — el mismo clip explicativo que usa el panel actual de Animar. */
const HERO_VIDEO = '/images/video_animar_imagen.mp4';

/** Copy del hero, espeja el tono de los otros modos. */
const HERO_DESC =
  'Convertí cualquier foto en un video: Atlas le da movimiento realista a la escena, con la duración que elijas.';

/** Duración por defecto del video, igual que el panel del historial. */
const DURACION_DEFAULT = 8;
const DURACION_MIN = 4;
const DURACION_MAX = 15;

/** Estimaciones para la barra de la pantalla de carga: Grok escena única tarda
 *  1-3 min; Kling multi-shot, 3-8. */
const CARGA_MS_ESCENA_UNICA = 180_000;
const CARGA_MS_MULTI_ESCENA = 480_000;

/** Cuántas veces se pollea la fila del video antes de mandar al historial igual. */
const POLL_INTENTOS = 15;
const POLL_INTERVALO_MS = 3000;

export default function AnimarImagenMode() {
  const navigate = useNavigate();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // ── Pantalla 1: elegir la imagen ──
  const [paso, setPaso] = useState<'elegir' | 'configurar'>('elegir');
  const [source, setSource] = useState<AnimarSource | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetGeneradas, setSheetGeneradas] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Cada selección invalida a las anteriores en vuelo: si una subida vieja termina
  // tarde no puede pisar la imagen que el usuario está mirando.
  const uploadSeqRef = useRef(0);

  // ── Pantalla 2: configurar y generar ──
  const [videoPrompt, setVideoPrompt] = useState('');
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [duracion, setDuracion] = useState(DURACION_DEFAULT);
  const [multiEscena, setMultiEscena] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [completeNow, setCompleteNow] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const pendingNavRef = useRef<{ videoId: string; companyId: string } | null>(null);
  const promptPedidoRef = useRef(false);

  // Paridad de cobro con el backend: Kling multi-escena cobra segundos × 2.
  const costo = multiEscena ? duracion * 2 : duracion;

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      setUserId(auth.user.id);
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      setCompanyId(
        (p as { id_company?: string | null } | null)?.id_company ?? null,
      );
    })();
  }, []);

  const volverAElegir = () => {
    setPaso('elegir');
    setSource(null);
    setPreviewUrl(null);
    setVideoPrompt('');
    setError(null);
    // Que la próxima imagen vuelva a pedir su propia descripción sugerida.
    promptPedidoRef.current = false;
  };

  // Back flotante del shell en mobile. Además de dar el botón, esconde la bottom
  // nav (MobileBottomNav se apaga con `mobileBackOverride !== null`), que es lo
  // que hacen el resto de los modos al ocupar la pantalla entera.
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'configurar') volverAElegir();
      else navigate('/modos');
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso]);

  // Descripción sugerida: se pide UNA vez, al entrar a configurar.
  useEffect(() => {
    if (paso !== 'configurar' || !source || !userId || promptPedidoRef.current) {
      return;
    }
    promptPedidoRef.current = true;
    setLoadingPrompt(true);
    fetchSuggestedVideoPromptForSource(source, userId)
      .then((p) => setVideoPrompt(p))
      .catch(() => setVideoPrompt(''))
      .finally(() => setLoadingPrompt(false));
  }, [paso, source, userId]);

  const elegirArchivo = () => fileInputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const seq = ++uploadSeqRef.current;
    setError(null);
    setSubiendo(true);
    try {
      const subida = await subirImagenParaAnimar(file, companyId);
      if (seq !== uploadSeqRef.current) return; // ganó una selección más nueva
      setSource({ tipo: 'subida', bucket: subida.bucket, path: subida.path });
      setPreviewUrl(subida.previewUrl);
      setPaso('configurar');
    } catch (err) {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      if (seq === uploadSeqRef.current) setSubiendo(false);
    }
  };

  const elegirGenerada = (pick: GeneradaPick) => {
    setSource({ tipo: 'generada', imageId: pick.id });
    setPreviewUrl(pick.signedUrl);
    setSheetGeneradas(false);
    setPaso('configurar');
  };

  const generar = async () => {
    if (!source || !userId || !companyId || generando) return;
    setGenerando(true);
    setError(null);
    setShowLoading(true);
    setCompleteNow(false);
    try {
      // Guards con el dato fresco de Supabase: plan (animar es Pro/Ultra, igual
      // que el botón del historial) y créditos suficientes para la duración.
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', companyId)
        .single();
      const plan = (perfil as { plan?: string } | null)?.plan;
      if (plan && !isProOrUltra(plan)) {
        setShowLoading(false);
        setGenerando(false);
        setError('Animar imágenes está disponible para los planes Pro y Ultra.');
        return;
      }
      const restantes = (perfil as { imagenes_restantes?: number | null } | null)
        ?.imagenes_restantes;
      if (typeof restantes === 'number' && restantes < costo) {
        setShowLoading(false);
        setGenerando(false);
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }

      const body = buildAnimarVideoBody(source, {
        userId,
        prompt: videoPrompt,
        duracion,
        multiplesEscenas: multiEscena,
      });
      const videoId = await generateVideoFromImage(body, { companyId });

      // La fila del video puede tardar en tener `file_name`: esperamos un poco
      // antes de mandar al historial, igual que el botón Animar de siempre.
      for (let intento = 1; intento <= POLL_INTENTOS; intento++) {
        const { data: fila } = await supabase
          .from('imagenes_generadas')
          .select('file_name')
          .eq('id', videoId)
          .eq('id_company', companyId)
          .single();
        if (fila?.file_name) break;
        await new Promise((r) => setTimeout(r, POLL_INTERVALO_MS));
      }

      pendingNavRef.current = { videoId, companyId };
      setCompleteNow(true); // cierra la barra de carga → onCompleted navega
    } catch (e) {
      console.error('[AnimarImagenMode] generar falló', e);
      setShowLoading(false);
      setGenerando(false);
      setError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'No se pudo generar el video. Intentá nuevamente.',
      );
    }
  };

  // ── Piezas compartidas entre mobile y desktop ────────────────────────────

  const heroVideo = (className: string) => (
    <video
      src={HERO_VIDEO}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      aria-label="Ejemplo del modo Animar imagen"
      className={className}
    />
  );

  const errorBox = error ? (
    <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
      {error}
    </div>
  ) : null;

  /** Encabezado + CTAs del aporte de imagen (mismo bloque que "Elegí el producto
   *  para generar" de los modos con producto). */
  const bloqueElegirImagen = (variant: 'mobile' | 'desktop') => (
    <>
      <div className="flex items-start gap-3.5 mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#1644EB]/10 flex items-center justify-center flex-shrink-0">
          <ImageUp className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black leading-tight">
            Elegí la imagen para animar
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            Atlas le va a dar movimiento a esta foto y devolverte un video.
          </p>
        </div>
      </div>

      {/* Con el flag de rollback a n8n no hay forma de animar una foto suelta:
          en ese caso queda sólo el camino de las generadas. */}
      {!SUBIDA_REQUIERE_ATLAS && (
        <button
          type="button"
          onClick={elegirArchivo}
          disabled={subiendo}
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-60 text-white text-[15.5px] font-semibold shadow-sm transition-colors"
        >
          {subiendo ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Subiendo…
            </>
          ) : (
            <>
              <ImageUp className="w-5 h-5" /> Subir imagen
            </>
          )}
        </button>
      )}

      {/* MOBILE: la grilla vive en un sheet, así el panel de abajo no se llena.
          DESKTOP: la grilla va inline debajo del separador. */}
      {variant === 'mobile' ? (
        <button
          type="button"
          onClick={() => setSheetGeneradas(true)}
          className="w-full mt-2.5 inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary text-brand-black text-[15.5px] font-semibold transition-colors"
        >
          <Images className="w-5 h-5" /> Elegir de mis generadas
        </button>
      ) : (
        <div className="flex items-center gap-3 mt-5 mb-1">
          <div className="flex-1 h-px bg-black/[0.06]" />
          <span className="text-[12px] text-[#3C3C43]/45 shrink-0">
            o elegí una que ya generaste
          </span>
          <div className="flex-1 h-px bg-black/[0.06]" />
        </div>
      )}
      {errorBox}
    </>
  );

  /** Opciones del video (pantalla 2). Igual en las dos variantes. */
  const bloqueConfigurar = (
    <>
      <p className="text-[13px] text-[#3C3C43]/60 uppercase tracking-wide mb-2">
        Descripción del video
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        {loadingPrompt ? (
          // Misma barra que el panel de Animar del historial / visor inline: la
          // sugerencia tarda ~10-15s y sin barra el spinner solo se siente colgado.
          // La animación es de duración fija (no refleja progreso real, igual que
          // el original) y `forwards` la deja llena si tarda de más.
          <div className="px-4 py-4">
            <div className="flex items-center gap-2 mb-3">
              <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
              <span className="text-[15px] text-[#3C3C43]/60">
                Generando idea para el video…
              </span>
            </div>
            <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1644EB] rounded-full"
                style={{ animation: 'videoPromptProgress 15s linear forwards' }}
              />
            </div>
            <style>{`@keyframes videoPromptProgress { from { width: 0%; } to { width: 100%; } }`}</style>
          </div>
        ) : (
          <div className="px-4 py-3">
            <textarea
              rows={4}
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              placeholder="Ej: que la cámara se acerque despacio mientras la luz cambia…"
              className="w-full text-[15px] text-brand-black placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
            />
          </div>
        )}
      </div>
      <p className="text-[13px] text-[#3C3C43]/60 mt-2">
        Cuanto más específico seas, mejor va a salir.
      </p>

      <p className="text-[13px] text-[#3C3C43]/60 uppercase tracking-wide mt-6 mb-2">
        Configuración
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        {/* Duración */}
        <div className="px-4 py-[11px]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[15px] text-brand-black">Duración</span>
            <span className="text-[15px] font-medium text-brand-black tabular-nums">
              {duracion} seg
            </span>
          </div>
          <input
            type="range"
            min={DURACION_MIN}
            max={DURACION_MAX}
            step={1}
            value={duracion}
            onChange={(e) => setDuracion(Number(e.target.value))}
            aria-label="Duración del video en segundos"
            className="w-full accent-brand-primary cursor-pointer"
          />
          <div className="flex items-center gap-2 mt-2">
            <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
            <span className="text-[13px] text-[#3C3C43]/60">
              Se consumirán {costo} imágenes
            </span>
          </div>
        </div>

        <div className="h-px bg-black/[0.08]" />

        {/* Múltiples escenas */}
        <div className="px-4 py-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-[15px] text-brand-black">Múltiples escenas</span>
            <button
              type="button"
              onClick={() => setMultiEscena(!multiEscena)}
              aria-pressed={multiEscena}
              aria-label="Múltiples escenas"
              className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${
                multiEscena ? 'bg-brand-primary' : 'bg-neutral-300'
              }`}
            >
              <span
                className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  multiEscena ? 'translate-x-[20px]' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {multiEscena && (
            <p className="text-[13px] text-[#3C3C43]/60 mt-2">
              El video se divide en varias escenas con transiciones automáticas.
              Consume el doble de imágenes.
            </p>
          )}
        </div>
      </div>
      {errorBox}
    </>
  );

  /** Botón Generar — sticky al fondo del panel de opciones, igual que los modos. */
  const botonGenerar = (
    <div className="shrink-0 bg-white px-5 pt-3 pb-5 border-t border-black/[0.06]">
      <button
        type="button"
        onClick={generar}
        disabled={generando}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
      >
        <Sparkles className="w-5 h-5" />
        Generar video
        <CreditBadge credits={costo} tone="on-primary" size="sm" />
      </button>
    </div>
  );

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      <OptionSheet
        open={sheetGeneradas}
        onClose={() => setSheetGeneradas(false)}
        title="Mis imágenes generadas"
        hideAccept
      >
        <GeneradasPickGrid
          companyId={companyId}
          selectedId={null}
          onSelect={elegirGenerada}
        />
      </OptionSheet>
      {showLoading && (
        <LoadingAnimation
          message="Generando video…"
          subMessage="Esto puede tardar unos minutos."
          durationMs={multiEscena ? CARGA_MS_MULTI_ESCENA : CARGA_MS_ESCENA_UNICA}
          completeNow={completeNow}
          onCompleted={() => {
            setShowLoading(false);
            setGenerando(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: [pend.videoId], companyId: pend.companyId },
              });
            }
          }}
        />
      )}
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );

  const enConfigurar = paso === 'configurar' && !!source && !!previewUrl;

  return (
    <>
      {/* ── MOBILE: carátula full-bleed + panel blanco redondeado abajo ────── */}
      <div className="md:hidden fixed inset-0 z-30 bg-surface-mobile flex flex-col">
        <div className="relative flex-1 min-h-0 bg-neutral-100">
          {enConfigurar ? (
            <CachedImg
              src={previewUrl as string}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
            />
          ) : (
            heroVideo('absolute inset-0 w-full h-full object-cover')
          )}
        </div>
        <div
          className="relative z-10 bg-white shrink-0 -mt-6 rounded-t-3xl flex flex-col overflow-hidden"
          style={{ maxHeight: enConfigurar ? '78dvh' : '55dvh' }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-7 pb-6">
            {enConfigurar ? bloqueConfigurar : bloqueElegirImagen('mobile')}
          </div>
          {enConfigurar && botonGenerar}
        </div>
      </div>

      {/* ── DESKTOP: panel a la derecha de la sidebar, dos columnas ────────── */}
      <div
        className="hidden md:flex fixed inset-y-0 right-0 z-30 bg-surface-content rounded-tl-[26px] rounded-bl-[26px] flex-col"
        style={{ left: sidebarWidth }}
      >
        <header className="shrink-0 px-6 pt-5 pb-3">
          <button
            type="button"
            onClick={() => (enConfigurar ? volverAElegir() : navigate('/modos'))}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
        </header>

        <div className="px-8 pb-6 flex flex-row gap-6 justify-center flex-1 min-h-0 items-stretch">
          {/* Columna izquierda: carátula del modo (o la imagen elegida) + copy. */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3 min-w-0">
            {enConfigurar ? (
              <CachedImg
                src={previewUrl as string}
                alt=""
                className="max-w-[480px] max-h-[70%] w-auto h-auto object-contain rounded-2xl"
              />
            ) : (
              <>
                {heroVideo(
                  'max-w-[360px] max-h-[60%] w-auto h-auto object-contain rounded-2xl',
                )}
                <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed text-center text-balance max-w-[360px]">
                  {HERO_DESC}
                </p>
              </>
            )}
          </div>

          {/* Columna derecha: card blanca de opciones. */}
          <aside className="shrink-0 flex-1 max-w-[560px] flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-6">
              {enConfigurar ? (
                bloqueConfigurar
              ) : (
                <>
                  {bloqueElegirImagen('desktop')}
                  <div className="mt-3">
                    <GeneradasPickGrid
                      companyId={companyId}
                      selectedId={null}
                      onSelect={elegirGenerada}
                    />
                  </div>
                </>
              )}
            </div>
            {enConfigurar && botonGenerar}
          </aside>
        </div>
      </div>

      {overlays}
    </>
  );
}
